import React, { useState, useEffect } from 'react';
import {
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Box,
  Button,
  TextField,
  Grid,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Snackbar,
  Alert,
  IconButton,
} from '@mui/material';
import {
  PictureAsPdf as PdfIcon,
  TableChart as ExcelIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import { exportToPDF, exportToExcel } from '../services/exportService';
import api from '../services/api';

function Clientes() {
  const [clientes, setClientes] = useState([]);
  const [busqueda, setBusqueda] = useState('');
  const [filtroTipo, setFiltroTipo] = useState('');
  
  // Estados para dialogs
  const [openDialog, setOpenDialog] = useState(false);
  const [openEliminar, setOpenEliminar] = useState(false);
  const [dialogMode, setDialogMode] = useState('crear'); // 'crear' o 'editar'
  const [clienteSeleccionado, setClienteSeleccionado] = useState(null);
  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  
  // Formulario
  const [formCliente, setFormCliente] = useState({
    tipo_documento: 'DNI',
    numero_documento: '',
    nombre: '',
    email: '',
    telefono: '',
    direccion: '',
    ciudad: 'Piura',
    tipo_cliente: 'individual'
  });

  useEffect(() => {
    cargarClientes();
  }, []);

  const cargarClientes = async () => {
    try {
      const response = await api.get('/clientes');
      setClientes(response.data.data);
    } catch (error) {
      console.error('Error cargando clientes:', error);
    }
  };

  // ============================================
  // ABRIR DIALOG CREAR
  // ============================================
  const handleOpenCrear = () => {
    setDialogMode('crear');
    setFormCliente({
      tipo_documento: 'DNI',
      numero_documento: '',
      nombre: '',
      email: '',
      telefono: '',
      direccion: '',
      ciudad: 'Piura',
      tipo_cliente: 'individual'
    });
    setOpenDialog(true);
  };

  // ============================================
  // ABRIR DIALOG EDITAR
  // ============================================
  const handleOpenEditar = (cliente) => {
    setDialogMode('editar');
    setClienteSeleccionado(cliente);
    setFormCliente({
      tipo_documento: cliente.tipo_documento || 'DNI',
      numero_documento: cliente.numero_documento || '',
      nombre: cliente.nombre || '',
      email: cliente.email || '',
      telefono: cliente.telefono || '',
      direccion: cliente.direccion || '',
      ciudad: cliente.ciudad || 'Piura',
      tipo_cliente: cliente.tipo_cliente || 'individual'
    });
    setOpenDialog(true);
  };

  // ============================================
  // ABRIR DIALOG ELIMINAR
  // ============================================
  const handleOpenEliminar = (cliente) => {
    setClienteSeleccionado(cliente);
    setOpenEliminar(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setClienteSeleccionado(null);
  };

  const handleCloseEliminar = () => {
    setOpenEliminar(false);
    setClienteSeleccionado(null);
  };

  const handleChange = (field, value) => {
    setFormCliente(prev => ({ ...prev, [field]: value }));
  };

  // ============================================
  // GUARDAR (CREAR O EDITAR)
  // ============================================
  const handleSubmit = async () => {
    if (!formCliente.nombre || !formCliente.numero_documento) {
      setSnackbar({ open: true, message: 'Nombre y número de documento son obligatorios', severity: 'warning' });
      return;
    }

    setLoading(true);
    try {
      if (dialogMode === 'crear') {
        await api.post('/clientes', formCliente);
        setSnackbar({ open: true, message: 'Cliente registrado correctamente', severity: 'success' });
      } else {
        await api.put(`/clientes/${clienteSeleccionado.id}`, formCliente);
        setSnackbar({ open: true, message: 'Cliente actualizado correctamente', severity: 'success' });
      }
      setOpenDialog(false);
      cargarClientes();
    } catch (error) {
      setSnackbar({ 
        open: true, 
        message: 'Error: ' + (error.response?.data?.message || error.message), 
        severity: 'error' 
      });
    } finally {
      setLoading(false);
    }
  };

  // ============================================
  // ELIMINAR CLIENTE
  // ============================================
  const handleEliminar = async () => {
    setLoading(true);
    try {
      await api.delete(`/clientes/${clienteSeleccionado.id}`);
      setSnackbar({ open: true, message: 'Cliente eliminado correctamente', severity: 'success' });
      setOpenEliminar(false);
      cargarClientes();
    } catch (error) {
      setSnackbar({ 
        open: true, 
        message: 'Error: ' + (error.response?.data?.message || error.message), 
        severity: 'error' 
      });
    } finally {
      setLoading(false);
      setClienteSeleccionado(null);
    }
  };

  const clientesFiltrados = clientes.filter(c => {
    const coincideBusqueda = !busqueda || 
      c.nombre.toLowerCase().includes(busqueda.toLowerCase()) || 
      c.numero_documento?.includes(busqueda) ||
      c.email?.toLowerCase().includes(busqueda.toLowerCase());
    const coincideTipo = !filtroTipo || c.tipo_cliente === filtroTipo;
    return coincideBusqueda && coincideTipo;
  });

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
          Gestión de Clientes
        </Typography>
        <Box>
          <Button
            variant="outlined"
            startIcon={<PdfIcon />}
            onClick={() => {
              const headers = ['Documento', 'Nombre', 'Email', 'Teléfono', 'Dirección', 'Ciudad', 'Tipo'];
              const data = clientes.map(c => [
                `${c.tipo_documento} ${c.numero_documento}`, c.nombre, c.email, c.telefono,
                c.direccion, c.ciudad, c.tipo_cliente
              ]);
              exportToPDF('Reporte de Clientes', headers, data, `clientes_${new Date().toISOString().split('T')[0]}`);
            }}
            sx={{ mr: 1, color: '#d32f2f', borderColor: '#d32f2f' }}
          >
            PDF
          </Button>
          <Button
            variant="outlined"
            startIcon={<ExcelIcon />}
            onClick={() => {
              const data = clientes.map(c => ({
                'Tipo Documento': c.tipo_documento,
                'Número': c.numero_documento,
                'Nombre': c.nombre,
                'Email': c.email,
                'Teléfono': c.telefono,
                'Dirección': c.direccion,
                'Ciudad': c.ciudad,
                'Tipo Cliente': c.tipo_cliente,
                'Estado': c.estado
              }));
              exportToExcel(data, `clientes_${new Date().toISOString().split('T')[0]}`, 'Clientes');
            }}
            sx={{ mr: 1, color: '#388e3c', borderColor: '#388e3c' }}
          >
            Excel
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleOpenCrear}
            sx={{ backgroundColor: '#1976d2' }}
          >
            Nuevo Cliente
          </Button>
        </Box>
      </Box>

      {/* Filtros */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={5}>
            <TextField
              fullWidth
              label="Buscar cliente..."
              placeholder="Nombre, documento o email"
              value={busqueda}
              onChange={(e) => setBusqueda(e.target.value)}
            />
          </Grid>
          <Grid item xs={12} md={5}>
            <TextField
              fullWidth
              select
              label="Tipo de Cliente"
              value={filtroTipo}
              onChange={(e) => setFiltroTipo(e.target.value)}
            >
              <MenuItem value="">Todos</MenuItem>
              <MenuItem value="individual">Individual</MenuItem>
              <MenuItem value="empresa">Empresa</MenuItem>
              <MenuItem value="institucion">Institución</MenuItem>
            </TextField>
          </Grid>
          <Grid item xs={12} md={2}>
            <Button 
              fullWidth 
              variant="outlined" 
              onClick={() => { setBusqueda(''); setFiltroTipo(''); }}
            >
              Limpiar
            </Button>
          </Grid>
        </Grid>
      </Paper>
      
      <TableContainer component={Paper}>
        <Table>
          <TableHead sx={{ backgroundColor: '#1976d2' }}>
            <TableRow>
              <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Documento</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Nombre</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Email</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Teléfono</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Ciudad</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Tipo</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Estado</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 'bold' }} align="center">Acciones</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {clientesFiltrados.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} align="center">
                  <Typography sx={{ py: 3 }}>No hay clientes registrados</Typography>
                </TableCell>
              </TableRow>
            ) : (
              clientesFiltrados.map((cliente) => (
                <TableRow key={cliente.id} hover>
                  <TableCell>{cliente.tipo_documento} {cliente.numero_documento}</TableCell>
                  <TableCell>{cliente.nombre}</TableCell>
                  <TableCell>{cliente.email}</TableCell>
                  <TableCell>{cliente.telefono}</TableCell>
                  <TableCell>{cliente.ciudad}</TableCell>
                  <TableCell>
                    <Chip label={cliente.tipo_cliente} color="info" size="small" />
                  </TableCell>
                  <TableCell>
                    <Chip label={cliente.estado} color="success" size="small" variant="outlined" />
                  </TableCell>
                  <TableCell align="center">
                    <IconButton 
                      size="small" 
                      color="primary" 
                      onClick={() => handleOpenEditar(cliente)}
                      sx={{ mr: 1 }}
                    >
                      <EditIcon />
                    </IconButton>
                    <IconButton 
                      size="small" 
                      color="error" 
                      onClick={() => handleOpenEliminar(cliente)}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* ============================================
          DIALOG: CREAR / EDITAR CLIENTE
          ============================================ */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle sx={{ color: '#1976d2', fontWeight: 'bold' }}>
          {dialogMode === 'crear' ? 'Registrar Nuevo Cliente' : 'Editar Cliente'}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                select
                label="Tipo de Documento"
                value={formCliente.tipo_documento}
                onChange={(e) => handleChange('tipo_documento', e.target.value)}
                required
              >
                <MenuItem value="DNI">DNI</MenuItem>
                <MenuItem value="RUC">RUC</MenuItem>
                <MenuItem value="PASAPORTE">Pasaporte</MenuItem>
                <MenuItem value="CE">Carnet de Extranjería</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Número de Documento"
                value={formCliente.numero_documento}
                onChange={(e) => handleChange('numero_documento', e.target.value)}
                required
                placeholder="Ej: 12345678"
                //disabled={dialogMode === 'editar'} // No permitir editar documento
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Nombre completo / Razón Social"
                value={formCliente.nombre}
                onChange={(e) => handleChange('nombre', e.target.value)}
                required
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Email"
                type="email"
                value={formCliente.email}
                onChange={(e) => handleChange('email', e.target.value)}
                placeholder="ejemplo@correo.com"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Teléfono"
                value={formCliente.telefono}
                onChange={(e) => handleChange('telefono', e.target.value)}
                placeholder="Ej: 073-123456"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Dirección"
                value={formCliente.direccion}
                onChange={(e) => handleChange('direccion', e.target.value)}
                placeholder="Ej: Av. Principal 123"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Ciudad"
                value={formCliente.ciudad}
                onChange={(e) => handleChange('ciudad', e.target.value)}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                select
                label="Tipo de Cliente"
                value={formCliente.tipo_cliente}
                onChange={(e) => handleChange('tipo_cliente', e.target.value)}
              >
                <MenuItem value="empresa">Empresa</MenuItem>
                <MenuItem value="individual">Individual</MenuItem>
                <MenuItem value="institucion">Institución</MenuItem>
              </TextField>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={handleCloseDialog} color="inherit" disabled={loading}>
            Cancelar
          </Button>
          <Button 
            onClick={handleSubmit} 
            variant="contained" 
            sx={{ backgroundColor: '#1976d2' }}
            disabled={loading}
          >
            {loading ? 'Guardando...' : (dialogMode === 'crear' ? 'Guardar Cliente' : 'Actualizar Cliente')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ============================================
          DIALOG: CONFIRMAR ELIMINAR
          ============================================ */}
      <Dialog open={openEliminar} onClose={handleCloseEliminar} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ color: '#d32f2f', fontWeight: 'bold' }}>
          Eliminar Cliente
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ mt: 1 }}>
            ¿Estás seguro de que deseas eliminar al cliente <strong>{clienteSeleccionado?.nombre}</strong>?
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Esta acción no se puede deshacer. El cliente se marcará como inactivo.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={handleCloseEliminar} color="inherit" disabled={loading}>
            Cancelar
          </Button>
          <Button 
            onClick={handleEliminar} 
            variant="contained" 
            color="error"
            disabled={loading}
          >
            {loading ? 'Eliminando...' : 'Eliminar'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar de notificaciones */}
      <Snackbar 
        open={snackbar.open} 
        autoHideDuration={4000} 
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default Clientes;